import { AttachmentPicker } from "./AttachmentPicker";
import { useEffect, useState } from "react";
import { api } from "../api/client";
import type { ImprovementRequest } from "../api/types";
import { errorMessage } from "../utils/errors";

interface Target {
  feedbackId?: string;
  x: number;
  y: number;
}

async function captureCurrentTab(target: Target): Promise<File> {
  if (!navigator.mediaDevices?.getDisplayMedia) {
    throw new Error("이 브라우저에서는 현재 탭 자동 캡처를 지원하지 않습니다.");
  }
  const sensitiveRegions = Array.from(document.querySelectorAll<HTMLElement>(
    "input, textarea, select, [data-feedback-private]",
  )).map((element) => element.getBoundingClientRect()).filter((rect) => (
    rect.width > 0
    && rect.height > 0
    && rect.bottom > 0
    && rect.right > 0
    && rect.top < window.innerHeight
    && rect.left < window.innerWidth
  )).map((rect) => ({
    x: Math.max(0, rect.left) / window.innerWidth,
    y: Math.max(0, rect.top) / window.innerHeight,
    width: Math.min(window.innerWidth, rect.right) / window.innerWidth
      - Math.max(0, rect.left) / window.innerWidth,
    height: Math.min(window.innerHeight, rect.bottom) / window.innerHeight
      - Math.max(0, rect.top) / window.innerHeight,
  }));
  const stream = await navigator.mediaDevices.getDisplayMedia({
    video: { displaySurface: "browser" },
    audio: false,
    preferCurrentTab: true,
    selfBrowserSurface: "include",
  } as DisplayMediaStreamOptions);
  try {
    const track = stream.getVideoTracks()[0];
    if (!track) throw new Error("공유된 화면을 읽지 못했습니다.");
    const surface = track.getSettings().displaySurface;
    if (surface && surface !== "browser") {
      throw new Error("다른 앱이 포함되지 않도록 브라우저의 현재 탭만 선택해주세요.");
    }

    const video = document.createElement("video");
    video.muted = true;
    video.playsInline = true;
    video.srcObject = stream;
    await video.play();
    if (!video.videoWidth || !video.videoHeight) {
      await new Promise<void>((resolve, reject) => {
        video.addEventListener("loadeddata", () => resolve(), { once: true });
        video.addEventListener("error", () => reject(new Error("캡처 화면을 준비하지 못했습니다.")), { once: true });
      });
    }

    const maxDimension = 1920;
    const scale = Math.min(1, maxDimension / Math.max(video.videoWidth, video.videoHeight));
    const width = Math.max(1, Math.round(video.videoWidth * scale));
    const height = Math.max(1, Math.round(video.videoHeight * scale));
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");
    if (!context) throw new Error("캡처 이미지를 만들지 못했습니다.");
    context.drawImage(video, 0, 0, width, height);

    context.fillStyle = "#dfe3e8";
    for (const region of sensitiveRegions) {
      context.fillRect(
        Math.floor(region.x * width),
        Math.floor(region.y * height),
        Math.ceil(region.width * width),
        Math.ceil(region.height * height),
      );
    }

    const markerX = Math.round(target.x * width);
    const markerY = Math.round(target.y * height);
    const markerRadius = Math.max(12, Math.round(Math.min(width, height) * 0.018));
    context.beginPath();
    context.arc(markerX, markerY, markerRadius, 0, Math.PI * 2);
    context.lineWidth = Math.max(4, Math.round(markerRadius * 0.25));
    context.strokeStyle = "#e02424";
    context.stroke();
    context.beginPath();
    context.moveTo(markerX - markerRadius * 1.4, markerY);
    context.lineTo(markerX + markerRadius * 1.4, markerY);
    context.moveTo(markerX, markerY - markerRadius * 1.4);
    context.lineTo(markerX, markerY + markerRadius * 1.4);
    context.stroke();

    const blob = await new Promise<Blob>((resolve, reject) => canvas.toBlob(
      (value) => value ? resolve(value) : reject(new Error("캡처 이미지를 저장하지 못했습니다.")),
      "image/png",
    ));
    return new File([blob], `improvement-screen-${Date.now()}.png`, { type: "image/png" });
  } finally {
    stream.getTracks().forEach((track) => track.stop());
  }
}

export function FeedbackLauncher() {
  const [open, setOpen] = useState(false);
  const [selecting, setSelecting] = useState(false);
  const [target, setTarget] = useState<Target>();
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [category, setCategory] = useState<ImprovementRequest["category"]>("IMPROVEMENT");
  const [visibility, setVisibility] = useState<ImprovementRequest["visibility"]>("COMPANY");
  const [file, setFile] = useState<File>();
  const [fileSource, setFileSource] = useState<"capture" | "manual">();
  const [previewUrl, setPreviewUrl] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string>();
  const [releaseSha, setReleaseSha] = useState<string>();

  useEffect(() => {
    void api.health().then((health) => setReleaseSha(health.releaseSha)).catch(() => undefined);
  }, []);

  useEffect(() => {
    if (!file?.type.startsWith("image/")) {
      setPreviewUrl(undefined);
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const selectTarget = () => {
    if (fileSource === "capture") {
      setFile(undefined);
      setFileSource(undefined);
    }
    setOpen(false);
    setSelecting(true);
    document.body.classList.add("feedback-selecting");
    const capture = async (event: MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      const element = event.target instanceof Element ? event.target : undefined;
      const marked = element?.closest<HTMLElement>("[data-feedback-id]");
      const fallback = element
        ? `tag:${element.tagName.toLowerCase()}`
        : undefined;
      const selectedTarget = {
        ...(marked?.dataset.feedbackId ? { feedbackId: marked.dataset.feedbackId } : fallback ? { feedbackId: fallback } : {}),
        x: Math.min(1, Math.max(0, event.clientX / window.innerWidth)),
        y: Math.min(1, Math.max(0, event.clientY / window.innerHeight)),
      };
      setTarget(selectedTarget);
      document.body.classList.remove("feedback-selecting");
      setSelecting(false);
      window.removeEventListener("click", capture, true);
      setMessage("선택한 위치를 표시한 현재 탭 캡처를 준비하고 있습니다.");
      try {
        const screenshot = await captureCurrentTab(selectedTarget);
        setFile(screenshot);
        setFileSource("capture");
        setMessage("현재 탭 캡처를 첨부했습니다. 접수 전에 미리보기를 확인해주세요.");
      } catch (error) {
        const denied = error instanceof DOMException && error.name === "NotAllowedError";
        setMessage(denied
          ? "화면 캡처를 건너뛰었습니다. 위치 정보만 보내거나 파일을 직접 첨부할 수 있습니다."
          : errorMessage(error));
      } finally {
        setOpen(true);
      }
    };
    window.setTimeout(() => window.addEventListener("click", capture, true), 0);
  };

  const submit = async () => {
    setBusy(true);
    setMessage(undefined);
    try {
      const hash = window.location.hash;
      const [route, query] = hash.replace(/^#/, "").split("?");
      const created = await api.createImprovement({
        title,
        body,
        category,
        visibility,
        priority: "NORMAL",
        sourceRoute: route || "/",
        ...(query ? { sourceQuery: query } : {}),
        ...(target?.feedbackId ? { sourceFeedbackId: target.feedbackId } : {}),
        ...(target ? { sourceX: target.x, sourceY: target.y } : {}),
        viewportWidth: window.innerWidth,
        viewportHeight: window.innerHeight,
        ...(releaseSha && releaseSha !== "0000000" ? { sourceBuildSha: releaseSha } : {}),
      });
      if (file) await api.uploadImprovementAttachment(created.id, file);
      setMessage(`${created.requestKey}로 접수했습니다.`);
      setTitle("");
      setBody("");
      setFile(undefined);
      setFileSource(undefined);
      setTarget(undefined);
    } catch (error) {
      setMessage(errorMessage(error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {selecting && <div className="feedback-select-banner">개선할 위치를 눌러주세요. 입력 내용은 수집하지 않습니다.</div>}
      <button
        type="button"
        className="feedback-launcher"
        data-feedback-id="feedback.launcher"
        onClick={() => setOpen(true)}
      >
        이 화면 개선 요청
      </button>
      {open && (
        <div className="dialog-backdrop" role="presentation" onMouseDown={(event) => {
          if (event.target === event.currentTarget) setOpen(false);
        }}>
          <section className="feedback-dialog" role="dialog" aria-modal="true" aria-labelledby="feedback-title">
            <header>
              <div><p className="eyebrow">IMPROVEMENT</p><h2 id="feedback-title">이 화면 개선 요청</h2></div>
              <button className="dialog-close" onClick={() => setOpen(false)} aria-label="닫기">×</button>
            </header>
            <label>제목<input value={title} maxLength={120} onChange={(event) => setTitle(event.target.value)} placeholder="무엇을 고치면 좋을까요?" /></label>
            <label>자세한 내용<textarea value={body} maxLength={5000} onChange={(event) => setBody(event.target.value)} placeholder="어떤 점이 불편하고, 어떻게 바뀌면 좋은지 적어주세요." /></label>
            <div className="feedback-dialog__row">
              <label>종류<select value={category} onChange={(event) => setCategory(event.target.value as ImprovementRequest["category"])}>
                <option value="IMPROVEMENT">기능 개선</option>
                <option value="BUG">오류</option>
                <option value="QUESTION">질문</option>
                <option value="CODE_QUALITY">코드 개선</option>
                <option value="OTHER">기타</option>
              </select></label>
              <label>공개 범위<select value={visibility} onChange={(event) => setVisibility(event.target.value as ImprovementRequest["visibility"])}>
                <option value="COMPANY">회사 전체</option>
                <option value="ADMIN_ONLY">관리자만</option>
              </select></label>
            </div>
            <button type="button" className="button button--secondary" onClick={selectTarget}>
              {target ? `위치 선택됨 · ${target.feedbackId ?? "화면"}` : "화면에서 위치 고르기"}
            </button>
            <small>위치를 고르면 브라우저가 현재 탭 공유 권한을 묻고 입력칸은 자동으로 가립니다. 다른 화면이나 앱은 선택하지 마세요.</small>
            <AttachmentPicker key={String(open)} label="스크린샷 또는 자료 (선택)" accept="image/png,image/jpeg,image/webp,application/pdf,text/plain"
              disabled={busy} busy={busy} files={file && fileSource !== "capture" ? [file] : []}
              onFiles={files => { setFile(files[0]); setFileSource("manual"); }}
              onRemove={() => { setFile(undefined); setFileSource(undefined); }} />
            {previewUrl && fileSource === "capture" && (
              <figure className="feedback-capture-preview">
                <img src={previewUrl} alt="첨부할 화면 캡처 미리보기" />
                <figcaption>
                  <span>{fileSource === "capture" ? "자동 캡처 · 빨간 표시가 선택 위치입니다." : file?.name}</span>
                  <button type="button" className="text-button" onClick={() => {
                    setFile(undefined);
                    setFileSource(undefined);
                  }}>첨부 제거</button>
                </figcaption>
              </figure>
            )}
            <small>첨부는 한 장만 전송되며 원본은 180일 뒤 삭제되고 파일 이름과 기록만 남습니다.</small>
            {message && <p className="form-message" role="status">{message}</p>}
            <footer>
              <button className="button button--secondary" onClick={() => setOpen(false)}>취소</button>
              <button className="button" disabled={busy || title.trim().length < 2 || body.trim().length < 2} onClick={() => void submit()}>
                {busy ? "접수 중…" : "개선요청 접수"}
              </button>
            </footer>
          </section>
        </div>
      )}
    </>
  );
}
